import { ActivityIcon } from '@jouzen/ecom-components/icons/ActivityIcon';
import { ActivityLinesIcon } from '@jouzen/ecom-components/icons/ActivityLinesIcon';
import { AlarmClockIcon } from '@jouzen/ecom-components/icons/AlarmClockIcon';
import { CircleFillIcon } from '@jouzen/ecom-components/icons/CircleFillIcon';
import { ClockForwardIcon } from '@jouzen/ecom-components/icons/ClockForwardIcon';
import { DataTrendsIcon } from '@jouzen/ecom-components/icons/DataTrendsIcon';
import { DotFillIcon } from '@jouzen/ecom-components/icons/DotFillIcon';
import { HeartWaveIcon } from '@jouzen/ecom-components/icons/HeartWaveIcon';
import { LocationIcon } from '@jouzen/ecom-components/icons/LocationIcon';
import { MoonIcon } from '@jouzen/ecom-components/icons/MoonIcon';
import { Spo2Icon } from '@jouzen/ecom-components/icons/Spo2Icon';
import { StepsIcon } from '@jouzen/ecom-components/icons/StepsIcon';
import { TemperatureIcon } from '@jouzen/ecom-components/icons/TemperatureIcon';
import { TimeDurationIcon } from '@jouzen/ecom-components/icons/TimeDurationIcon';
import { WaterDropIcon } from '@jouzen/ecom-components/icons/WaterDropIcon';
import { WavesIcon } from '@jouzen/ecom-components/icons/WavesIcon';
import { InverseHeader } from '@jouzen/ecom-components/layout';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import {
  SwipeableProgressBar,
  SwipeableProvider,
  SwipeableSlider,
} from '@jouzen/ecom-components/v3/SwipeableSlider';
import { Fragment } from 'react';

import BackdropBlur from '@/app/components/BackdropBlur';
import Image, { type ImageProps } from '@/app/components/Image';
import Motion, { type MotionProps } from '@/app/components/Motion';
import SwipeableNavButtons from '@/app/components/SwipeableNavButtons';

import MeasureCard, { type MeasureCardProps } from './MeasureCard';

const getFadeMotionProps = (index: number): MotionProps => {
  return {
    initial: {
      opacity: 0,
    },
    transition: {
      delay: 0.1 * index,
      duration: 0.75,
      type: 'tween',
    },
    viewport: {
      amount: 0.33,
      once: true,
    },
    whileInView: {
      opacity: 1,
    },
  };
};

const IMAGE_HEIGHT = 660;
const IMAGE_WIDTH = 375;

interface Card {
  image: ImageProps;
  ui: MeasureCardProps;
}

const cards: Card[] = [
  {
    image: {
      height: IMAGE_HEIGHT,
      localizedAlt: 'membership_sleep_card_tag',
      src: '/blue-sky/science-and-research/sleep-rest.jpg',
      width: IMAGE_WIDTH,
    },
    ui: {
      title: 'sleep_rest_tag',
      subtitle: 'science_research_2025_card_one_subtitle',
      list: [
        {
          icon: MoonIcon,
          listItem: 'science_research_2025_sleep_timing',
        },
        {
          icon: WavesIcon,
          listItem: 'science_research_2025_nighttime_movement',
        },
        {
          icon: ClockForwardIcon,
          listItem: 'science_research_2025_sleep_regularity',
        },
        {
          icon: DataTrendsIcon,
          listItem: 'science_research_2025_sleep_stages',
        },
        {
          icon: Spo2Icon,
          listItem: 'science_research_2025_spo2',
        },
      ],
    },
  },
  {
    image: {
      height: IMAGE_HEIGHT,
      localizedAlt: 'fitness_tag',
      src: '/blue-sky/science-and-research/activity-fitness.jpg',
      width: IMAGE_WIDTH,
    },
    ui: {
      title: 'fitness_tag',
      subtitle: 'science_research_2025_card_two_subtitle',
      list: [
        {
          icon: ActivityIcon,
          listItem: 'science_research_2025_calories_burned',
        },
        {
          icon: StepsIcon,
          listItem: 'science_research_2025_step_count',
        },
        {
          icon: ActivityLinesIcon,
          listItem: 'science_research_2025_activity_level',
        },
        {
          icon: LocationIcon,
          listItem: 'science_research_2025_distance',
        },
        {
          icon: TimeDurationIcon,
          listItem: 'science_research_2025_average_pace',
        },
        {
          icon: HeartWaveIcon,
          listItem: 'science_research_2025_average_heart_rate',
        },
      ],
    },
  },
  {
    image: {
      height: IMAGE_HEIGHT,
      localizedAlt: 'membership_women_health_card_tag',
      src: '/blue-sky/science-and-research/womens-health.jpg',
      width: IMAGE_WIDTH,
    },
    ui: {
      title: 'womens_health_tag',
      subtitle: 'science_research_2025_card_three_subtitle',
      list: [
        {
          icon: TemperatureIcon,
          listItem: 'science_research_2025_temp_trends',
        },
        {
          icon: CircleFillIcon,
          listItem: 'science_research_2025_fertile_window',
        },
        {
          icon: DotFillIcon,
          listItem: 'science_research_2025_cycle_day',
        },
        {
          icon: AlarmClockIcon,
          listItem: 'science_research_2025_cycle_phase',
        },
        {
          icon: WaterDropIcon,
          listItem: 'science_research_2025_period_prediction',
        },
      ],
    },
  },
];

const MeasureCards = () => {
  return (
    <InverseHeader enter>
      <SwipeableProvider>
        <div className="flex flex-col-reverse lg:hidden">
          <div className="relative mt-6 mr-6">
            <SwipeableProgressBar variant="light" />
            <SwipeableNavButtons />
          </div>
          <SwipeableSlider className="pr-6">
            {cards.map(({ image, ui }) => (
              <div
                className="relative h-[137vw] w-[75vw] md:h-[82vw] md:w-[45vw]"
                key={image.localizedAlt}
              >
                <Image {...image} className="w-full rounded-lg" />
                <div className="absolute bottom-0 left-1/2 min-h-[345px] w-[90%] -translate-x-1/2">
                  <BackdropBlur variant="card" />
                  <div className="relative p-4">
                    <MeasureCard {...ui} />
                  </div>
                </div>
              </div>
            ))}
          </SwipeableSlider>
        </div>
      </SwipeableProvider>
      <div className="hidden grid-cols-3 gap-6 lg:grid">
        {cards.map(({ image, ui }, index) => {
          const colStartValue = (index + 1) as 1 | 2 | 3;
          const colEndValue = (index + 2) as 2 | 3 | 4;
          const fadeMotionProps = getFadeMotionProps(index);

          return (
            <Fragment key={image.localizedAlt}>
              <GridItem
                colEnd={{ sm: colEndValue }}
                colStart={{ sm: colStartValue }}
                rowEnd={{ sm: 3 }}
                rowStart={{ sm: 1 }}
              >
                <Motion className="size-full" {...fadeMotionProps}>
                  <Image {...image} className="w-full rounded-lg" />
                </Motion>
              </GridItem>
              <GridItem
                className="flex justify-center"
                colEnd={{ sm: colEndValue }}
                colStart={{ sm: colStartValue }}
                rowEnd={{ sm: 3 }}
                rowStart={{ sm: 2 }}
              >
                <div className="relative -bottom-6 row-start-2 row-end-3 flex h-full w-[80%] p-4">
                  <BackdropBlur variant="card" {...fadeMotionProps} />
                  <Motion className="relative size-full" {...fadeMotionProps}>
                    <MeasureCard {...ui} />
                  </Motion>
                </div>
              </GridItem>
            </Fragment>
          );
        })}
      </div>
    </InverseHeader>
  );
};

export default MeasureCards;
